import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMailAdminAuth } from '../../hooks/useMailAdminAuth'
import { listDomains, createDomain, deleteDomain, type Domain } from '../../lib/jmap-admin'

export function Domains() {
  const { auth, logout } = useMailAdminAuth()
  const navigate = useNavigate()
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!auth) { navigate('/admin/login'); return }
    loadDomains()
  }, [auth])

  const loadDomains = async () => {
    if (!auth) return
    setLoading(true)
    try {
      setDomains(await listDomains(auth))
    } catch (e) {
      setError(`Failed to load domains: ${e}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!auth || !newDomain.trim()) return
    setCreating(true)
    setError('')
    try {
      await createDomain(auth, newDomain.trim())
      setNewDomain('')
      setShowCreate(false)
      await loadDomains()
    } catch (e) {
      setError(`Failed to create domain: ${e}`)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!auth || !confirm(`Delete domain "${name}"? This cannot be undone.`)) return
    try {
      await deleteDomain(auth, id)
      await loadDomains()
    } catch (e) {
      setError(`Failed to delete domain: ${e}`)
    }
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-text">Domains</h1>
          <p className="text-text-muted mt-1">Manage email domains on this server</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadDomains}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-text-muted hover:bg-slate-50 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700 transition-colors"
          >
            Add Domain
          </button>
          <button
            onClick={logout}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-text-muted hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-6 bg-surface rounded-xl border border-slate-200 p-6">
          <h3 className="font-ui font-semibold text-text mb-4">Add new domain</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-sovereign-500 focus:ring-2 focus:ring-sovereign-500/20"
              autoFocus
            />
            <button
              type="submit"
              disabled={creating || !newDomain.trim()}
              className="px-6 py-2.5 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setNewDomain('') }}
              className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-text-muted hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-12 text-text-muted">Loading domains...</div>
      ) : domains.length === 0 ? (
        <div className="text-center py-16 bg-surface rounded-xl border border-slate-200">
          <div className="text-4xl mb-4">🌐</div>
          <h3 className="font-ui text-lg font-semibold text-text mb-2">No domains yet</h3>
          <p className="text-text-muted mb-6">Add your first email domain to get started</p>
          <button
            onClick={() => setShowCreate(true)}
            className="px-6 py-2.5 bg-sovereign-600 text-white rounded-lg text-sm font-medium hover:bg-sovereign-700 transition-colors"
          >
            Add Domain
          </button>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-slate-200 divide-y divide-slate-200">
          {domains.map((domain) => (
            <div key={domain.id} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-sovereign-50 flex items-center justify-center">
                  <span className="text-sovereign-600 font-semibold text-sm">
                    {domain.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <button
                    onClick={() => navigate(`/admin/domains/${domain.id}`)}
                    className="font-ui font-semibold text-text hover:text-sovereign-600 transition-colors"
                  >
                    {domain.name}
                  </button>
                  {domain.description && (
                    <p className="text-xs text-text-muted">{domain.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  domain.enabled ? 'bg-trust/10 text-trust' : 'bg-slate-100 text-text-muted'
                }`}>
                  {domain.enabled ? 'Active' : 'Disabled'}
                </span>
                <button
                  onClick={() => navigate(`/admin/domains/${domain.id}`)}
                  className="text-sm text-text-muted hover:text-text transition-colors"
                >
                  Manage
                </button>
                <button
                  onClick={() => handleDelete(domain.id, domain.name)}
                  className="text-sm text-red-500 hover:text-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
